<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useConversationPins } from 'dashboard/composables/useConversationPins';
import ConversationFollowUpModal from './ConversationFollowUpModal.vue';
import ConversationMessageSearch from './ConversationMessageSearch.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

const props = defineProps({
  isWhatsappGroup: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const followUpMode = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](currentChat.value?.inbox_id)
);
const { isConversationPinned, toggleConversationPin } = useConversationPins();
const isPinned = computed(() => isConversationPinned(currentChat.value.id));

const actionMenuItems = computed(() => {
  const items = [];

  items.push({
    icon: 'i-lucide-pin',
    label: isPinned.value
      ? t('CONVERSATION.PIN.UNPIN')
      : t('CONVERSATION.PIN.PIN'),
    action: 'toggle_pin',
    value: 'toggle_pin',
  });

  items.push({
    icon: 'i-lucide-bell-plus',
    label: t('OPERATIONS.COMPOSER.REMINDER'),
    action: 'reminder',
    value: 'reminder',
  });

  items.push({
    icon: 'i-lucide-calendar-clock',
    label: t('OPERATIONS.COMPOSER.MESSAGE'),
    action: 'schedule_message',
    value: 'schedule_message',
  });

  if (!props.isWhatsappGroup && !currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else if (!props.isWhatsappGroup) {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'toggle_pin') {
    toggleConversationPin(currentChat.value.id);
    useAlert(
      isPinned.value
        ? t('CONVERSATION.PIN.PINNED_SUCCESS')
        : t('CONVERSATION.PIN.UNPINNED_SUCCESS')
    );
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'reminder') {
    followUpMode.value = 'reminder';
  } else if (action === 'schedule_message') {
    followUpMode.value = 'message';
  }
};

// These functions are needed for the event listeners
const mute = () => {
  if (props.isWhatsappGroup) return;

  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  if (props.isWhatsappGroup) return;

  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      v-if="
        !isWhatsappGroup && !currentInbox?.assign_conversations_to_all_agents
      "
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <ConversationMessageSearch :conversation-id="currentChat.id" />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <ConversationFollowUpModal
      v-if="followUpMode"
      :conversation-id="currentChat.id"
      :initial-mode="followUpMode"
      @close="followUpMode = null"
    />
  </div>
</template>
